import React, { useState } from 'react';
import { User } from '@/types';
import DeleteUserModal from '@/components/modals/DeleteUserModal';

interface UsersTabProps {
    users: User[];
    isDark: boolean;
    setShowCreateUserModal: (show: boolean) => void;
    setShowEditUserModal: (user: { user: User } | false) => void;
    setShowAssignUserModal: (user: { user: User } | false) => void;
    deleteUser: (userId: number) => Promise<boolean>;
    fetchDashboardData: () => Promise<void>;
}

const UsersTab: React.FC<UsersTabProps> = ({
    users,
    isDark,
    setShowCreateUserModal,
    setShowEditUserModal,
    setShowAssignUserModal,
    deleteUser,
    fetchDashboardData
}) => {
    const [showDeleteModal, setShowDeleteModal] = useState<{ user: User } | false>(false);

    const handleDeleteUser = async (user: User) => {
        setShowDeleteModal({ user });
    };

    const handleConfirmDelete = async () => {
        if (showDeleteModal) {
            const success = await deleteUser(showDeleteModal.user.id);
            if (success) {
                await fetchDashboardData();
            }
            return success;
        }
        return false;
    };

    return (
        <>
            {showDeleteModal && (
                <DeleteUserModal
                    user={showDeleteModal.user}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={handleConfirmDelete}
                />
            )}
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} backdrop-blur-lg rounded-2xl border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                <div className={`p-6 border-b ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'}`}>
                    <div className="flex justify-between items-center">
                        <h3 className="text-2xl font-bold cgm-text-primary">User Management</h3>
                        <button
                            onClick={() => setShowCreateUserModal(true)}
                            className="cgm-button px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105"
                        >
                            Create User
                        </button>
                    </div>
                </div>
                <div className="overflow-x-auto rounded-b-2xl">
                    <table className="min-w-full">
                        <thead className={`${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                            <tr>
                                <th className="px-6 py-4 text-left text-sm font-semibold cgm-text-primary">User</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold cgm-text-primary">Role</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold cgm-text-primary">Teams</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold cgm-text-primary">Status</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold cgm-text-primary">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user, index) => (
                                <tr key={user.id} className={`${isDark ? 'hover:bg-gray-700/30' : 'hover:bg-gray-100'} transition-colors ${index % 2 === 0 ? '' : isDark ? 'bg-gray-800/25' : 'bg-white'}`}>
                                    <td className="px-6 py-4">
                                        <div>
                                            <div className="text-sm font-medium cgm-text-primary">
                                                {user.full_name || user.username}
                                            </div>
                                            <div className="text-sm cgm-text-secondary">{user.email}</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-wrap gap-2">
                                            {/* Global role badge */}
                                            <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${user.role === 'admin' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                                                user.role === 'coach' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                                                    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                }`}>
                                                {user.role}
                                            </span>
                                            {/* Add unique team roles if they differ from global role */}
                                            {Array.from(new Set((user.teams || []).map(t => t.role)))
                                                .filter(teamRole => teamRole && teamRole !== user.role)
                                                .map(teamRole => (
                                                    <span key={teamRole} className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${teamRole === 'coach' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'}`}>
                                                        {teamRole}
                                                    </span>
                                                ))}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm cgm-text-primary">
                                            {user.teams && user.teams.length > 0 ? (
                                                user.teams.map((team, i) => {
                                                    const roleSet = new Set<string>();
                                                    if (team.role) roleSet.add(team.role);
                                                    if (user.role && user.role !== team.role) roleSet.add(user.role);
                                                    const rolesDisplay = Array.from(roleSet).join(', ');
                                                    return (
                                                        <div key={i} className="mb-1">
                                                            <span className="font-medium">{team.team_name}</span>
                                                            <span className="cgm-text-secondary"> ({rolesDisplay})</span>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <span className="cgm-text-secondary">No teams</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${(user.whoop_connected || user.garmin_connected)
                                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                                            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                                            }`}>
                                            <span className={`cgm-status-dot ${(user.whoop_connected || user.garmin_connected) ? 'cgm-status-connected' : 'cgm-status-warning'}`} />
                                            <span>{(user.whoop_connected || user.garmin_connected) ? 'Connected' : 'Not Connected'}</span>
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <button
                                            onClick={() => setShowEditUserModal({ user })}
                                            className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200 text-sm font-medium transition-colors mr-4"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => setShowAssignUserModal({ user })}
                                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 text-sm font-medium transition-colors mr-4"
                                        >
                                            Assign to Team
                                        </button>
                                        <button
                                            onClick={() => handleDeleteUser(user)}
                                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 text-sm font-medium transition-colors"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

export default UsersTab;